/**
 * Architecture Executor
 *
 * Handles the 'architecture' node execution.
 * Calls the ModernizeIT API Layer /architecture/analyze endpoint.
 *
 * API Flow:
 * 1. POST /architecture/analyze → returns job_id and results
 * 2. GET /architecture/{job_id}/status → check status
 * 3. GET /architecture/{job_id}/results → retrieve full results
 *
 * This node provides evidence-based AWS architecture recommendations:
 * - Loads data from 5 sources (Discovery, Data Analysis, Code Analysis, Code Refactor, Java)
 * - Analyzes generated Java code for dependencies and patterns
 * - Cross-validates sources to build confidence
 * - Generates AWS architecture recommendations with cost estimates
 * - Generates IaC templates (CDK)
 *
 * Note: This API uses Form data (not JSON body)
 *
 * Recommendations produced:
 * - compute_recommendation (Lambda, ECS, EC2)
 * - database_recommendation (Aurora, RDS, DynamoDB)
 * - api_recommendation (API Gateway)
 * - storage_recommendation (S3, EFS)
 * - security_recommendation (VPC, encryption)
 * - cost_estimate (monthly/annual)
 * - iac_templates (CDK TypeScript)
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the architecture node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and recommendations
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing architecture analysis...')

  // Check for abort before starting
  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // 1. Build the form data (Architecture API uses multipart form)
  const formData = new FormData()
  formData.append('scout_account_id', context.scout_account_id)
  formData.append('application_name', context.application_name)
  formData.append('user_id', context.user_id) // REQUIRED 2026-01-14: For AWS credentials lookup
  formData.append('generate_iac', config.generate_iac !== false ? 'true' : 'false')

  // Optional source paths and job references
  if (config.source_path) {
    formData.append('source_path', config.source_path)
  }
  if (config.java_source_path) {
    formData.append('java_source_path', config.java_source_path)
  }
  if (config.discovery_job_id) {
    formData.append('discovery_job_id', config.discovery_job_id)
  }
  if (config.data_analysis_job_id) {
    formData.append('data_analysis_job_id', config.data_analysis_job_id)
  }
  if (config.code_analysis_job_id) {
    formData.append('code_analysis_job_id', config.code_analysis_job_id)
  }
  if (config.code_refactor_job_id) {
    formData.append('code_refactor_job_id', config.code_refactor_job_id)
  }

  onProgress(5, 'Submitting architecture analysis job...')

  // 2. Submit the job
  let jobResponse
  try {
    const response = await fetch(`${apiBaseUrl}/architecture/analyze`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    // Check for API-level error in response
    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'Architecture analysis failed')
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  const jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  onProgress(10, `Job started: ${jobId}`)

  // 3. Check if job completed immediately (synchronous execution)
  if (jobResponse.status === 'completed' || jobResponse.status === 'SUCCEEDED') {
    onProgress(50, 'Architecture analysis complete')
  } else {
    // 4. Worker mode: Poll for completion using shared authenticated poller
    onProgress(15, 'Processing architecture analysis...')
    const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
      maxWaitSeconds: 600,
      pollInterval: 2000,
      onProgress: (progress, message) => {
        // Map polling progress from 15-95%
        const mappedProgress = 15 + (progress * 0.80)
        onProgress(mappedProgress, message)
      },
      signal
    })

    // Merge completed job data
    Object.assign(jobResponse, completedJob)
  }

  onProgress(100, 'Architecture analysis complete')

  // 5. Return formatted result with job data
  return formatResult(jobId, jobResponse)
}

/**
 * Format the result object for consistent structure
 */
function formatResult(jobId, data) {
  return {
    // Job tracking
    jobs: {
      architecture_job_id: jobId,
    },

    // Status
    success: data.success !== false,
    status: data.status || 'completed',

    // Paths (note: architecture uses output_path instead of artifacts_path)
    source_path: data.source_path,
    artifacts_path: data.output_path,
    output_path: data.output_path,

    // Execution metrics (note: architecture uses duration_seconds)
    duration_ms: data.duration_seconds ? data.duration_seconds * 1000 : data.duration_ms,
    duration_seconds: data.duration_seconds,

    // Summary
    summary: data.summary || {},

    // Recommendations
    recommendations: {
      compute: data.compute_recommendation || {},
      database: data.database_recommendation || {},
      api: data.api_recommendation || {},
      storage: data.storage_recommendation || {},
      security: data.security_recommendation || {},
    },

    // Cost estimate
    cost_estimate: data.cost_estimate || {},

    // Validation
    validation_report: data.validation_report || {},
    warnings: data.warnings || [],

    // Traceability (Java class to AWS service mappings)
    traceability: data.traceability || {},

    // IaC templates
    iac_templates: data.iac_templates || null,

    // Migration phases
    migration_phases: data.migration_phases || [],

    // Java analysis details
    java_analysis: data.java_analysis || null,

    // Sources used for recommendations
    sources_used: data.sources_used || [],

    // Generated timestamp
    generated_at: data.generated_at || null,

    // Error info if any
    error: data.error || null,
  }
}

export default {
  execute,
}
